/**
 * Complaint Detail Page
 * Display detailed complaint information and admin actions
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Link,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Warning as WarningIcon,
    Block as BlockIcon,
    HighlightOff as RejectIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { ComplaintService } from '../../../services/complaint/ComplaintService';
import type { ComplaintDetail, ComplaintStatus, ComplaintCategory, ResolutionAction } from '../../../types/complaint';
import { formatDateTime } from '../../../utils/dateUtils';
import {
    Card,
    CardContent,
    TextField,
    PrimaryButton,
} from '../../../components/ui';
import { useMessage } from '../../../contexts/MessageContext';

const statusLabels: Record<ComplaintStatus, string> = {
    pending: 'Under Review',
    resolved: 'Resolved',
    rejected: 'Rejected',
};

const categoryLabels: Record<ComplaintCategory, string> = {
    harassment: 'Harassment',
    inappropriate_content: 'Inappropriate Content',
    spam: 'Spam',
    fraud: 'Fraud',
    other: 'Other',
};

const resolutionLabels: Record<ResolutionAction, string> = {
    WarnUser: 'Warning Issued',
    BlockUser: 'User Suspended',
};

const getStatusColor = (status: ComplaintStatus, theme: any): string => {
    switch (status) {
        case 'pending':
            return theme.palette.warning.main;
        case 'resolved':
            return theme.palette.success.main;
        case 'rejected':
            return theme.palette.error.main;
        default:
            return theme.palette.text.secondary;
    }
};

const getCategoryColor = (category: ComplaintCategory, theme: any): string => {
    switch (category) {
        case 'harassment':
            return theme.palette.error.main;
        case 'inappropriate_content':
            return theme.palette.error.light;
        case 'spam':
            return theme.palette.warning.main;
        case 'fraud':
            return theme.palette.error.dark;
        case 'other':
            return theme.palette.grey[500];
        default:
            return theme.palette.text.secondary;
    }
};

const ComplaintDetailPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { showSuccess, showError } = useMessage();
    const { id } = useParams<{ id: string }>();
    const [complaint, setComplaint] = useState<ComplaintDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dialog states
    const [warnDialogOpen, setWarnDialogOpen] = useState(false);
    const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [actionNotes, setActionNotes] = useState('');

    const isClosed = complaint?.status === 'resolved' || complaint?.status === 'rejected';
    const outcomeLabel = complaint?.status === 'rejected'
        ? 'Complaint Rejected'
        : complaint?.resolutionAction
            ? resolutionLabels[complaint.resolutionAction]
            : 'Complaint Resolved';

    const refreshComplaintDetail = async (complaintId: string) => {
        const detail = await ComplaintService.getComplaintDetail(complaintId);
        setComplaint(detail);
    };

    const resetActionDialog = (setOpen: (open: boolean) => void) => {
        setOpen(false);
        setActionNotes('');
    };

    useEffect(() => {
        const fetchComplaintDetail = async () => {
            if (!id) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const detail = await ComplaintService.getComplaintDetail(id);
                setComplaint(detail);
            } catch (error) {
                console.error('Failed to fetch complaint detail:', error);
                setComplaint(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchComplaintDetail();
    }, [id]);

    const handleWarnUser = async () => {
        if (!id || !actionNotes.trim()) {
            showError('Please provide notes for the warning.');
            return;
        }

        try {
            setIsSubmitting(true);
            await ComplaintService.issueWarning(id, actionNotes);
            showSuccess('Warning issued successfully.');
            resetActionDialog(setWarnDialogOpen);
            await refreshComplaintDetail(id);
        } catch (error) {
            console.error('Failed to issue warning:', error);
            showError(error instanceof Error ? error.message : 'Failed to issue warning.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSuspendUser = async () => {
        if (!id || !actionNotes.trim()) {
            showError('Please provide notes for the suspension.');
            return;
        }

        try {
            setIsSubmitting(true);
            await ComplaintService.suspendUser(id, actionNotes);
            showSuccess('User suspended successfully.');
            resetActionDialog(setSuspendDialogOpen);
            await refreshComplaintDetail(id);
        } catch (error) {
            console.error('Failed to suspend user:', error);
            showError(error instanceof Error ? error.message : 'Failed to suspend user.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRejectComplaint = async () => {
        if (!id || !actionNotes.trim()) {
            showError('Please provide the rejection reason.');
            return;
        }

        try {
            setIsSubmitting(true);
            await ComplaintService.rejectComplaint(id, actionNotes);
            showSuccess('Complaint rejected successfully.');
            resetActionDialog(setRejectDialogOpen);
            await refreshComplaintDetail(id);
        } catch (error) {
            console.error('Failed to reject complaint:', error);
            showError(error instanceof Error ? error.message : 'Failed to reject complaint.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <MainLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
                    <LoadingSpinner />
                </Box>
            </MainLayout>
        );
    }

    if (!complaint) {
        return (
            <MainLayout>
                <EmptyState
                    title="Complaint Not Found"
                    message="The complaint you're looking for doesn't exist or has been removed."
                />
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ marginBottom: theme.spacing(2) }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/complaints')}
                    sx={{
                        color: theme.palette.text.secondary,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                        cursor: 'pointer',
                    }}
                >
                    Complaints
                </Link>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    Complaint Details
                </Typography>
            </Breadcrumbs>

            {/* Complaint Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(3),
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Complaint: {complaint.missionTitle}
                    </Typography>
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            padding: theme.spacing(0.5, 1.5),
                            borderRadius: theme.shapes.borderRadius.lg,
                            backgroundColor: getStatusColor(complaint.status, theme),
                            color: theme.palette.common.white,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                        }}
                    >
                        {statusLabels[complaint.status]}
                    </Box>
                </Box>
            </Box>

            {/* Created Date */}
            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(3),
                }}
            >
                Created on: {formatDateTime(complaint.createdAt)}
            </Typography>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Left Column - Main Content */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
                        {/* Complaint Summary */}
                        <Card>
                            <CardContent>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        marginBottom: theme.spacing(2),
                                        color: theme.palette.text.primary,
                                        fontSize: '1rem',
                                    }}
                                >
                                    Complaint Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: theme.typography.caption.fontSize,
                                                marginBottom: theme.spacing(0.5),
                                            }}
                                        >
                                            Category:
                                        </Typography>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-block',
                                                padding: theme.spacing(0.5, 1.5),
                                                borderRadius: theme.shapes.borderRadius.lg,
                                                backgroundColor: getCategoryColor(complaint.category, theme),
                                                color: theme.palette.common.white,
                                                fontWeight: 500,
                                                fontSize: theme.typography.caption.fontSize,
                                            }}
                                        >
                                            {categoryLabels[complaint.category]}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: theme.typography.caption.fontSize,
                                                marginBottom: theme.spacing(0.5),
                                            }}
                                        >
                                            Mission Code:
                                        </Typography>
                                        <Link
                                            component="button"
                                            variant="body2"
                                            onClick={() => navigate(`/missions/${complaint.missionId}`)}
                                            sx={{
                                                color: theme.palette.primary.main,
                                                cursor: 'pointer',
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                                fontSize: theme.typography.body2.fontSize,
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            {complaint.missionCode}
                                        </Link>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: theme.typography.caption.fontSize,
                                                marginBottom: theme.spacing(0.5),
                                            }}
                                        >
                                            Reporter:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 500,
                                                fontSize: theme.typography.body2.fontSize,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            {complaint.reporterName}
                                            <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 400, fontSize: '0.85em' }}>
                                                ({complaint.reporterEmail})
                                            </Box>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: theme.typography.caption.fontSize,
                                                marginBottom: theme.spacing(0.5),
                                            }}
                                        >
                                            Reported User:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 500,
                                                fontSize: theme.typography.body2.fontSize,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            {complaint.reportedUserName}
                                            <Box component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 400, fontSize: '0.85em' }}>
                                                ({complaint.reportedUserEmail})
                                            </Box>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: theme.typography.caption.fontSize,
                                                marginBottom: theme.spacing(0.5),
                                            }}
                                        >
                                            Requested Block:
                                        </Typography>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-block',
                                                padding: theme.spacing(0.5, 1.5),
                                                borderRadius: theme.shapes.borderRadius.lg,
                                                backgroundColor: complaint.requestedBlockUser ? theme.palette.error.main : theme.palette.grey[400],
                                                color: theme.palette.common.white,
                                                fontWeight: 500,
                                                fontSize: theme.typography.caption.fontSize,
                                            }}
                                        >
                                            {complaint.requestedBlockUser ? 'Yes' : 'No'}
                                        </Box>
                                    </Grid>
                                    {complaint.reasonDetail && (
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontSize: theme.typography.caption.fontSize,
                                                    marginBottom: theme.spacing(0.5),
                                                }}
                                            >
                                                Reason Detail:
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    fontSize: theme.typography.body2.fontSize,
                                                    lineHeight: 1.6,
                                                    padding: theme.spacing(1.5),
                                                    backgroundColor: theme.customColors.searchBackground,
                                                    borderRadius: 1,
                                                }}
                                            >
                                                {complaint.reasonDetail}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Review Outcome */}
                        {isClosed && (
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            marginBottom: theme.spacing(2),
                                            color: theme.palette.text.primary,
                                            fontSize: '1rem',
                                        }}
                                    >
                                        Review Outcome
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontSize: theme.typography.caption.fontSize,
                                                    marginBottom: theme.spacing(0.5),
                                                }}
                                            >
                                                Decision:
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                    fontSize: theme.typography.body2.fontSize,
                                                }}
                                            >
                                                {outcomeLabel}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontSize: theme.typography.caption.fontSize,
                                                    marginBottom: theme.spacing(0.5),
                                                }}
                                            >
                                                {complaint.status === 'rejected' ? 'Rejected By:' : 'Resolved By:'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                    fontSize: theme.typography.body2.fontSize,
                                                }}
                                            >
                                                {complaint.resolvedByEmail || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontSize: theme.typography.caption.fontSize,
                                                    marginBottom: theme.spacing(0.5),
                                                }}
                                            >
                                                {complaint.status === 'rejected' ? 'Rejected At:' : 'Resolved At:'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                    fontSize: theme.typography.body2.fontSize,
                                                }}
                                            >
                                                {complaint.resolvedAt ? formatDateTime(complaint.resolvedAt) : 'N/A'}
                                            </Typography>
                                        </Grid>
                                        {complaint.resolutionNotes && (
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        fontSize: theme.typography.caption.fontSize,
                                                        marginBottom: theme.spacing(0.5),
                                                    }}
                                                >
                                                    {complaint.status === 'rejected' ? 'Rejection Reason:' : 'Notes:'}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                        fontSize: theme.typography.body2.fontSize,
                                                        lineHeight: 1.6,
                                                        padding: theme.spacing(1.5),
                                                        backgroundColor: theme.customColors.searchBackground,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    {complaint.resolutionNotes}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </Grid>

                {/* Right Column - Admin Actions */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: theme.spacing(2),
                                    color: theme.palette.text.primary,
                                    fontSize: '1rem',
                                }}
                            >
                                Admin Actions
                            </Typography>

                            {isClosed ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    This complaint has been {complaint.status === 'rejected' ? 'rejected' : 'resolved'}. No further actions available.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: '0.875rem',
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        Take action on this complaint:
                                    </Typography>

                                    <PrimaryButton
                                        onClick={() => setWarnDialogOpen(true)}
                                        startIcon={<WarningIcon />}
                                        sx={{
                                            backgroundColor: theme.palette.warning.main,
                                            '&:hover': {
                                                backgroundColor: theme.palette.warning.dark,
                                            },
                                        }}
                                    >
                                        Issue Warning
                                    </PrimaryButton>

                                    <PrimaryButton
                                        onClick={() => setSuspendDialogOpen(true)}
                                        startIcon={<BlockIcon />}
                                        sx={{
                                            backgroundColor: theme.palette.error.main,
                                            '&:hover': {
                                                backgroundColor: theme.palette.error.dark,
                                            },
                                        }}
                                    >
                                        Suspend User
                                    </PrimaryButton>

                                    <PrimaryButton
                                        onClick={() => setRejectDialogOpen(true)}
                                        startIcon={<RejectIcon />}
                                        sx={{
                                            backgroundColor: theme.palette.grey[700],
                                            '&:hover': {
                                                backgroundColor: theme.palette.grey[800],
                                            },
                                        }}
                                    >
                                        Reject Complaint
                                    </PrimaryButton>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Warn Dialog */}
            <Dialog open={warnDialogOpen} onClose={() => resetActionDialog(setWarnDialogOpen)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Issue Warning</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ marginBottom: theme.spacing(2), color: theme.palette.text.secondary }}>
                        You are about to issue a warning to <strong>{complaint?.reportedUserName}</strong> ({complaint?.reportedUserEmail}).
                        Please provide notes for this action.
                    </Typography>
                    <TextField
                        label="Notes"
                        multiline
                        rows={4}
                        fullWidth
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Describe the reason for the warning..."
                    />
                </DialogContent>
                <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
                    <Button onClick={() => resetActionDialog(setWarnDialogOpen)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <PrimaryButton
                        onClick={handleWarnUser}
                        disabled={isSubmitting || !actionNotes.trim()}
                        sx={{
                            backgroundColor: theme.palette.warning.main,
                            '&:hover': {
                                backgroundColor: theme.palette.warning.dark,
                            },
                        }}
                    >
                        {isSubmitting ? 'Issuing...' : 'Issue Warning'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            {/* Suspend Dialog */}
            <Dialog open={suspendDialogOpen} onClose={() => resetActionDialog(setSuspendDialogOpen)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Suspend User</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ marginBottom: theme.spacing(2), color: theme.palette.text.secondary }}>
                        You are about to suspend the account of <strong>{complaint?.reportedUserName}</strong> ({complaint?.reportedUserEmail}).
                        This action will prevent them from accessing the platform. Please provide notes for this action.
                    </Typography>
                    <TextField
                        label="Notes"
                        multiline
                        rows={4}
                        fullWidth
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Describe the reason for the suspension..."
                    />
                </DialogContent>
                <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
                    <Button onClick={() => resetActionDialog(setSuspendDialogOpen)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <PrimaryButton
                        onClick={handleSuspendUser}
                        disabled={isSubmitting || !actionNotes.trim()}
                        sx={{
                            backgroundColor: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: theme.palette.error.dark,
                            },
                        }}
                    >
                        {isSubmitting ? 'Suspending...' : 'Suspend User'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectDialogOpen} onClose={() => resetActionDialog(setRejectDialogOpen)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Reject Complaint</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ marginBottom: theme.spacing(2), color: theme.palette.text.secondary }}>
                        You are about to reject the complaint against <strong>{complaint?.reportedUserName}</strong> ({complaint?.reportedUserEmail}).
                        Please provide the reason for rejecting this complaint.
                    </Typography>
                    <TextField
                        label="Rejection Reason"
                        multiline
                        rows={4}
                        fullWidth
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Describe why this complaint is being rejected..."
                    />
                </DialogContent>
                <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
                    <Button onClick={() => resetActionDialog(setRejectDialogOpen)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <PrimaryButton
                        onClick={handleRejectComplaint}
                        disabled={isSubmitting || !actionNotes.trim()}
                        sx={{
                            backgroundColor: theme.palette.grey[700],
                            '&:hover': {
                                backgroundColor: theme.palette.grey[800],
                            },
                        }}
                    >
                        {isSubmitting ? 'Rejecting...' : 'Reject Complaint'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </MainLayout>
    );
};

export default ComplaintDetailPage;
